import { getRandomNegativeId } from "@/shared/utils/helpers";
import { useEffect, useRef } from "react";
import usePosSessionOrdersHook from "@/shared/hooks/usePosSessionOrdersHook";
import useConvertCartContentToCartHook from "./useConvertCartContentToCartHook";
import useSplitPaymentHook, {
  defaultLedgerPayment,
} from "./useSplitPaymentHook";
import { useCart } from "./useCartHook";
import {
  useReadSplitPaymentOrdersState,
  useSetSplitPaymentOrdersState,
} from "../atoms/useSplitPaymentV2Atom";

const useSplitPaymentEffectsHook = () => {
  const sessionCartRef = useRef([]);
  const splitPaymentCartRef = useRef([]);
  const [cart] = useCart();
  const orders = usePosSessionOrdersHook();
  const convertCartContentToCart = useConvertCartContentToCartHook();
  const { createUnpaidLedgers } = useSplitPaymentHook();
  const { splitCount, splitType, paymentLedgers } =
    useReadSplitPaymentOrdersState();
  const setSplitPaymentState = useSetSplitPaymentOrdersState();

  useEffect(() => {
    if (orders?.length > 0) {
      const reversed = [...orders].reverse();
      reversed.forEach((order) => {
        const cart = convertCartContentToCart(order.cart_content);
        sessionCartRef.current.push(
          ...cart.map((c) => ({ ...c, item_id: getRandomNegativeId() }))
        );
      });
    }

    // required for all cart items to have unique id
    splitPaymentCartRef.current =
      sessionCartRef.current.length > 0
        ? sessionCartRef.current
        : cart.map((item) => ({ ...item, item_id: getRandomNegativeId() }));
  }, []);

  useEffect(() => {
    if (splitType !== "cart") return;
    const ledgerCount = paymentLedgers.length;
    if (ledgerCount > splitCount) {
      const unpaidLedgers = paymentLedgers.filter(
        (ledger) => !ledger.is_paid && ledger.item_id < 0
      );
      if (unpaidLedgers.length > 0) {
        const lastUnpaidLedger = unpaidLedgers[unpaidLedgers.length - 1];
        const newLedgerPayment = paymentLedgers.filter(
          (ledger) => ledger.item_id !== lastUnpaidLedger.item_id
        );
        setSplitPaymentState({ paymentLedgers: newLedgerPayment });
      }
    } else {
      const newLedgerPayment = Array(splitCount - ledgerCount)
        .fill(null)
        .map(() => ({
          ...defaultLedgerPayment,
          item_id: getRandomNegativeId(),
        }));

      setSplitPaymentState({
        paymentLedgers: [...paymentLedgers, ...newLedgerPayment],
      });
    }
  }, [splitCount]);

  useEffect(() => {
    if (splitType !== "custom") return;
    const paidLedgers = paymentLedgers.filter((l) => l.is_paid);
    const unpaidLedgers = createUnpaidLedgers(paidLedgers, splitCount);

    let unpaidIndex = 0;
    const list = Array.from({ length: splitCount }, (_, index) => {
      const ledger = paymentLedgers[index];
      return ledger?.item_id > 0 ? ledger : unpaidLedgers[unpaidIndex++];
    });

    console.log("list", list);

    setSplitPaymentState({ paymentLedgers: list });
  }, [splitCount]);

  return { splitPaymentCart: splitPaymentCartRef.current };
};

export default useSplitPaymentEffectsHook;
